import * as React from 'react';
import * as _ from 'lodash';
import * as styles from './modal.module.scss';
import Comment from '../../../models/comment';
import CommentDisplay from './comment-display';
import { withStyles } from '@material-ui/core/styles';
import Menu, { MenuProps } from '@material-ui/core/Menu';
import { MenuItem, ListItemText } from '@material-ui/core';
import TertiaryButton from '../../common/tertiary-button';
import { fontFamilies } from '../../../theme/text';

interface Props {
  comments: Comment[];
}

const StyledMenu = withStyles({
  paper: {
    border: '1px solid #d3d4d5'
  }
})((props: MenuProps) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center'
    }}
    {...props}
  />
));

const StyledMenuItem = withStyles(theme => ({
  root: {
    '&:focus': {
      backgroundColor: theme.palette.primary.main,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: theme.palette.common.white
      }
    },
    '& .MuiTypography-body1': {
      fontFamily: fontFamilies.body,
      fontWeight: 200,
      fontSize: 14,
      letterSpacing: '0.2px',
      color: '#000000'
    },
    '& .MuiListItem-button:hover': {
      backgroundColor: '#eaf3f8'
    }
  },
  selected: {
    backgroundColor: '#eaf3f8'
  }
}))(MenuItem);

/**
 * CommentSection
 * @function CommentSection - Outer component to display comments in modal
 */
const CommentSection = ({ comments }: Props) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement>(null);
  const [commentsState, setCommentsState] = React.useState<Comment[]>([]);
  const [sortAscending, setSortAscending] = React.useState<boolean>(true);

  React.useEffect(() => {
    // Sorts comments on mount, and when the amount of comments changes
    sortComments(sortAscending, comments);
  }, [comments && comments.length]);

  /**
   * @function sortComments - Sorts comments and closes menu
   * @param ascending Boolean indicating whether to sort comment ascending/descending
   * @param updatedComments Comments to be updated
   */
  const sortComments = (ascending: boolean, updatedComments?: Comment[]) => {
    if (ascending) {
      setAnchorEl(null);
      setSortAscending(true);
      if (updatedComments) {
        const commentsClone = _.cloneDeep(updatedComments);
        return setCommentsState(commentsClone.sort((a, b) => (b.createdTime >= a.createdTime ? 1 : -1)));
      }
      return setCommentsState(arr => arr.sort((a, b) => (b.createdTime >= a.createdTime ? 1 : -1)));
    }
    setSortAscending(false);
    setAnchorEl(null);
    if (updatedComments) {
      const commentsClone = _.cloneDeep(updatedComments);
      return setCommentsState(commentsClone.sort((a, b) => (a.createdTime >= b.createdTime ? 1 : -1)));
    }
    return setCommentsState(arr => arr.sort((a, b) => (a.createdTime >= b.createdTime ? 1 : -1)));
  };

  return (
    <div className={styles.commentsSection}>
      <div className={styles.header}>
        <h6>Comments</h6>
        {comments.length > 1 && (
          <TertiaryButton text="Sort" title="Sort" handleClick={event => setAnchorEl(event.currentTarget)} />
        )}
      </div>
      {commentsState.length > 0 ? (
        <div className={styles.commentsMap}>
          {commentsState.map((comment, index) => (
            <CommentDisplay key={`comment-${index}`} comment={comment} />
          ))}
        </div>
      ) : (
        <span className={styles.noResults}>No comments present</span>
      )}
      <StyledMenu
        id="customized-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <StyledMenuItem selected={sortAscending} onClick={() => sortComments(true)}>
          <ListItemText primary="Newest to Oldest" />
        </StyledMenuItem>
        <StyledMenuItem selected={!sortAscending} onClick={() => sortComments(false)}>
          <ListItemText primary="Oldest to Newest" />
        </StyledMenuItem>
      </StyledMenu>
    </div>
  );
};

export default CommentSection;
